import { getCustomerSupportChat, getCustomerSupportChatCount } from 'Api/endpoints/CustomerSupportChat';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import useInterval from 'Util/useInterval';
import Content from './Content';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import CommonDialog from 'Components/Popup/CommonDialog';
import { useSelector } from 'react-redux';
import Header from './Header';
import useFaq from './hooks/useFaq';

const CustomerSupportChat = () => {
  const [comments, setComments] = useState([]);
  const [commentsCount, setCommentsCount] = useState(0);
  const { formatMessage } = useIntl();
  const { dialogData } = useSelector(state => state.loading);
  const { faq, postFaq } = useFaq();
  const [isOpenFaq, setIsOpenFaq] = useState(true);

  const pageRef = useRef(1);
  const [isLoading, setIsLoading] = useState(false);

  const getComments = useCallback(async (page = 1) => {
    try {
      const res = await getCustomerSupportChat({ page });
      if (page === 1) {
        setCommentsCount(res?.count);
      }
      return res;
    } catch (error) {
      console.error(error);
    }
  }, []);

  const resetComments = useCallback(async () => {
    const result = await getComments(1);
    if (!result) return;
    setComments(result?.items || []);
    pageRef.current = 2;
    // BroadcastChannel을 통해 다른 창/탭에 noty refresh 요청 전송
    const channel = new BroadcastChannel('naya-noty-refresh');
    channel.postMessage('refresh-noty');
    channel.close();
  }, [getComments]);

  useEffect(() => {
    resetComments();
    setIsLoading(false);
  }, [resetComments]);

  const getList = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await getComments(pageRef.current);
      const items = Array.isArray(result?.items) ? result.items : [];
      if (items.length > 0) {
        setComments(prev => [...prev, ...items]);
        pageRef.current += 1;
      }
    } finally {
      setIsLoading(false);
    }
  }, [getComments]);

  const getCommentsWithPage = useCallback(async () => {
    const allItems = [];
    if (pageRef.current === 1) {
      const data = await getComments(1);
      if (!data) return;
      const items = Array.isArray(data.items) ? data.items : [];
      if (items.length > 0) {
        allItems.push(...items);
      }
    }
    for (let i = 1; i <= pageRef.current - 1; i++) {
      const data = await getComments(i);
      if (!data) continue;
      const items = Array.isArray(data.items) ? data.items : [];
      if (items.length > 0) {
        allItems.push(...items);
      }
    }
    setComments(allItems);
  }, [getComments]);

  const getCommentCountAndInfo = useCallback(async () => {
    try {
      const result = await getCustomerSupportChatCount();
      if (!result) return;
      if (result?.count !== commentsCount) {
        getCommentsWithPage();
      }
    } catch (error) {
      console.error(error);
    }
  }, [getCommentsWithPage, commentsCount]);

  useInterval(() => {
    getCommentCountAndInfo();
  }, 2000);

  return (
    <div className='customer-support-chat-page'>
      <Helmet>
        <title>{formatMessage({ id: 'customer.support.chat.header' })}</title>
        <meta name='description' content={formatMessage({ id: 'customer.support.chat.header' })} />
      </Helmet>
      <Header setIsOpenFaq={setIsOpenFaq} />
      <Content
        comments={comments}
        getList={getList}
        isLoading={isLoading}
        refreshInputComment={() => {
          resetComments();
          setIsLoading(false);
        }}
        faq={faq}
        postFaq={postFaq}
        isOpenFaq={isOpenFaq}
        setIsOpenFaq={setIsOpenFaq}
      />
      <CommonDialog {...dialogData} />
    </div>
  );
};

export default CustomerSupportChat;
